import { Link } from '@tanstack/react-router';
import { Layers, Mail, MessageSquarePlus, Phone, Plus, Star, TriangleAlert, User } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { LANGUAGE_PROFICIENCY_LABELS, type CandidateDetail, type CandidateProfile } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Chip } from '../../../../components/Chip';
import { SearchInput } from '../../../../components/Input';
import { MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { ToneChip } from '../../../../components/Tone';
import { errorMessage } from '../../../../lib/api-client';
import { formatDate, formatIsoDate, initialsOf } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { useAddCandidateTag, useRemoveCandidateTag, useTagOptions, useToggleStar } from '../../api/candidate-browse-api';
import { educationLabel, experienceLabel, yesNo } from '../../candidate-format';
import { moneyText, salaryLines } from './panel-format';
import type { PanelLogKind } from './ActivityTab';
import styles from './CandidatePanel.module.css';

const TAG_SUGGESTIONS = 8;
const TAG_MAX = 40;
const SALARY_FIELD = 'expectedSalaryMinor';

interface PanelAsideProps {
  readonly profile: CandidateProfile;
  /** Message, Call and "Log a call or a note" switch to Activity on that kind. */
  readonly onLog: (kind: PanelLogKind) => void;
  readonly onShowJobs: () => void;
  readonly isCollapsed: boolean;
  readonly onToggleCollapsed: () => void;
}

/**
 * The pop-up's left side (CandidatePanel.dc.html "About"): who they are, the star, Message and
 * Call, tags, the duplicate notice, the 13 details in the profile's order, skills. A viewer who
 * may not change the person (a hiring manager) reads it only: no star, no tags to change, no logging.
 */
export function PanelAside({ profile, onLog, onShowJobs, isCollapsed, onToggleCollapsed }: PanelAsideProps): ReactNode {
  const { candidate, can } = profile;
  return (
    <aside className={styles.aside} aria-label={`About ${candidate.fullName}`} data-collapsed={isCollapsed || undefined}>
      <div className={styles.asideScroll}>
        <div className={styles.who}>
          <span className={styles.avatar} aria-hidden>
            {initialsOf(candidate.fullName)}
          </span>
          <div className={styles.whoText}>
            <div className={styles.nameRow}>
              <h2 className={styles.name}>{candidate.fullName}</h2>
              {can.edit && <StarButton candidateId={candidate.id} isStarred={profile.isStarred} name={candidate.fullName} />}
            </div>
            <p className={styles.sub}>
              Added {formatDate(candidate.createdAt)}
              {candidate.createdByName ? ` by ${candidate.createdByName}` : ''}
            </p>
            {candidate.doNotRehire && <ToneChip tone="rejected">Do not rehire</ToneChip>}
          </div>
          <button type="button" className={styles.collapse} aria-expanded={!isCollapsed} onClick={onToggleCollapsed}>
            {isCollapsed ? 'Show details' : 'Hide details'}
          </button>
        </div>

        {can.edit && (
          <div className={styles.contactButtons}>
            <Button variant="solid" icon={Mail} onClick={() => onLog('email')}>
              Message
            </Button>
            <Button variant="outline" icon={Phone} onClick={() => onLog('call')}>
              Call
            </Button>
          </div>
        )}

        <div className={styles.collapsible}>
          <Tags profile={profile} />

          {candidate.mergedIntoCandidateId && (
            <p className={styles.notice}>
              <TriangleAlert size={14} aria-hidden />
              <span>
                This record was merged into another.{' '}
                <Link to="/candidates/$candidateId" params={{ candidateId: candidate.mergedIntoCandidateId }}>
                  Open the surviving record
                </Link>
              </span>
            </p>
          )}
          {profile.openFlags.map((flag) => (
            <p key={flag.id} className={styles.notice}>
              <TriangleAlert size={14} aria-hidden />
              <span>
                May be the same person as <strong>{flag.otherName}</strong> ({flag.confidence}% match).{' '}
                <Link to="/candidates/duplicates/$flagId" params={{ flagId: flag.id }}>
                  Review
                </Link>
              </span>
            </p>
          ))}

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <User size={16} aria-hidden />
              Details
            </h3>
            <Facts profile={profile} onShowJobs={onShowJobs} />
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <Layers size={16} aria-hidden />
              Skills
            </h3>
            <Skills candidate={candidate} />
          </section>
        </div>
      </div>
      {can.edit && (
        <div className={styles.asideFoot}>
          <Button variant="outline" block icon={MessageSquarePlus} onClick={() => onLog('note')}>
            Log a call or a note
          </Button>
        </div>
      )}
    </aside>
  );
}

function StarButton({ candidateId, isStarred, name }: { readonly candidateId: string; readonly isStarred: boolean; readonly name: string }): ReactNode {
  const toggle = useToggleStar();
  const show = useToasts((state) => state.show);
  return (
    <button
      type="button"
      className={styles.star}
      data-on={isStarred || undefined}
      aria-pressed={isStarred}
      aria-label={isStarred ? `Remove the star from ${name}` : `Star ${name}`}
      onClick={() =>
        toggle.mutate(
          { candidateId, isStarred: !isStarred },
          { onError: (error) => show({ tone: 'danger', message: "The star didn't change", detail: errorMessage(error) }) },
        )
      }
    >
      <Star size={18} aria-hidden />
    </button>
  );
}

// --- Tags ------------------------------------------------------------------------------------

function Tags({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  const candidateId = profile.candidate.id;
  const canEdit = profile.can.edit;
  const remove = useRemoveCandidateTag();
  const show = useToasts((state) => state.show);
  if (!canEdit && profile.tags.length === 0) return null;
  return (
    <div className={styles.tags}>
      {profile.tags.map((tag) => (
        <Chip
          key={tag.id}
          plain
          {...(canEdit
            ? {
                removeLabel: `Remove the tag ${tag.name}`,
                onRemove: () =>
                  remove.mutate({ candidateId, tagId: tag.id }, { onError: (error) => show({ tone: 'danger', message: `${tag.name} is still on`, detail: errorMessage(error) }) }),
              }
            : {})}
        >
          {tag.name}
        </Chip>
      ))}
      {canEdit && <AddTag profile={profile} />}
    </div>
  );
}

/** Pick an existing tag or type a new one; it shows at once (§5a rule 1). */
function AddTag({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState('');
  const options = useTagOptions(isOpen);
  const add = useAddCandidateTag();
  const show = useToasts((state) => state.show);
  const trimmed = query.trim();
  const onIt = new Set(profile.tags.map((tag) => tag.name.toLowerCase()));
  const matching = (options.data ?? []).filter((tag) => !onIt.has(tag.name.toLowerCase()) && tag.name.toLowerCase().includes(trimmed.toLowerCase())).slice(0, TAG_SUGGESTIONS);
  const isNew = trimmed !== '' && !(options.data ?? []).some((tag) => tag.name.toLowerCase() === trimmed.toLowerCase()) && !onIt.has(trimmed.toLowerCase());

  const pick = (input: { readonly tagId?: string; readonly name?: string }, name: string): void => {
    close();
    setQuery('');
    add.mutate(
      { candidateId: profile.candidate.id, input, optimistic: { id: input.tagId ?? `new-${name}`, name } },
      { onError: (error) => show({ tone: 'danger', message: `${name} wasn't added`, detail: errorMessage(error) }) },
    );
  };

  return (
    <>
      <button ref={triggerRef} type="button" className={styles.addTag} aria-expanded={isOpen} aria-haspopup="dialog" onClick={toggle}>
        <Plus size={14} aria-hidden />
        Add tag
      </button>
      {isOpen && (
        <Popover anchor={anchor} onClose={close} label="Add a tag">
          <form
            className={styles.popoverBody}
            onSubmit={(event) => {
              event.preventDefault();
              const exact = (options.data ?? []).find((tag) => tag.name.toLowerCase() === trimmed.toLowerCase());
              if (exact && !onIt.has(exact.name.toLowerCase())) pick({ tagId: exact.id }, exact.name);
              else if (isNew) pick({ name: trimmed }, trimmed);
            }}
          >
            <SearchInput value={query} autoFocus maxLength={TAG_MAX} placeholder="Find or type a new tag" aria-label="Tag" onChange={(event) => setQuery(event.target.value)} />
          </form>
          {matching.map((tag) => (
            <MenuItem key={tag.id} onClick={() => pick({ tagId: tag.id }, tag.name)}>
              {tag.name}
            </MenuItem>
          ))}
          {isNew && (
            <MenuItem icon={<Plus size={14} aria-hidden />} onClick={() => pick({ name: trimmed }, trimmed)}>
              Add “{trimmed}”
            </MenuItem>
          )}
          {!isNew && matching.length === 0 && <p className={`${styles.quiet} ${styles.popoverNote}`}>{options.data ? 'Type a new tag.' : 'Loading tags…'}</p>}
        </Popover>
      )}
    </>
  );
}

// --- Details ---------------------------------------------------------------------------------

/** The profile's 13 rows in its order; a field the viewer may not see is "—" (#5, §L23). */
function Facts({ profile, onShowJobs }: { readonly profile: CandidateProfile; readonly onShowJobs: () => void }): ReactNode {
  const { candidate } = profile;
  const restricted = new Set(candidate.restrictedFields ?? []);
  const shown = (value: string | null, field?: string): ReactNode =>
    field && restricted.has(field) ? <span title="Not visible to your role">—</span> : (value ?? '—');
  const rows: readonly (readonly [string, ReactNode])[] = [
    ['Current role', shown([candidate.currentTitle, candidate.currentEmployer].filter(Boolean).join(' at ') || null)],
    ['Experience', shown(experienceLabel(candidate.totalExperienceMonths))],
    ['Highest education', shown(educationLabel(candidate.highestEducation))],
    ['Expected salary', restricted.has(SALARY_FIELD) ? shown(null, SALARY_FIELD) : <Salary key="salary" profile={profile} onShowJobs={onShowJobs} />],
    ['Notice period', shown(candidate.noticePeriodDays === null ? null : `${candidate.noticePeriodDays} days`)],
    ['Willing to relocate', shown(yesNo(candidate.isWillingToRelocate))],
    ['Email', shown(candidate.email, 'email')],
    ['Mobile', shown(candidate.phone, 'phone')],
    ['City', shown(candidate.city, 'city')],
    ['LinkedIn', shown(candidate.linkedinUrl, 'linkedinUrl')],
    ['Date of birth', shown(candidate.dateOfBirth ? formatIsoDate(candidate.dateOfBirth) : null, 'dateOfBirth')],
    ['Gender', shown(candidate.gender)],
    ['NID', shown(candidate.nationalId, 'nationalId')],
  ];
  return (
    <dl className={styles.facts}>
      {rows.map(([label, value]) => (
        <div key={label} className={styles.fact}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One line per job (owner, 2026-10-17), or the person's own figure when they applied to none. */
function Salary({ profile, onShowJobs }: { readonly profile: CandidateProfile; readonly onShowJobs: () => void }): ReactNode {
  const { lines, more } = salaryLines(profile.applications);
  if (lines.length === 0) {
    return <SalaryOwn candidate={profile.candidate} />;
  }
  return (
    <div className={styles.salary}>
      {lines.map((line) => (
        <span key={line.key} className={styles.salaryLine} data-over={line.isOver || undefined}>
          <b className={styles.num}>{line.amount}</b>
          <span>{line.job}</span>
          {line.isOver && <ToneChip tone="urgent">{line.overText}</ToneChip>}
          {line.note !== '' && <span className={styles.sub}>{line.note}</span>}
        </span>
      ))}
      {more > 0 && (
        <button type="button" className={styles.more} onClick={onShowJobs}>
          and {more} more in Jobs applied
        </button>
      )}
    </div>
  );
}

function SalaryOwn({ candidate }: { readonly candidate: CandidateDetail }): ReactNode {
  const amount = moneyText(candidate.expectedSalaryMinor, candidate.expectedSalaryCurrency);
  if (amount === null) return '—';
  return (
    <span className={styles.salaryLine}>
      <b className={styles.num}>{amount}</b> a month
    </span>
  );
}

function Skills({ candidate }: { readonly candidate: CandidateDetail }): ReactNode {
  const languages = candidate.languages
    .map((row) => (row.proficiency ? `${row.language} (${LANGUAGE_PROFICIENCY_LABELS[row.proficiency].toLowerCase()})` : row.language))
    .join(', ');
  return (
    <>
      {candidate.skills.length > 0 ? (
        <div className={styles.skills}>
          {candidate.skills.map((skill) => (
            <span key={skill} className={styles.skill}>
              {skill}
            </span>
          ))}
        </div>
      ) : (
        <p className={styles.quiet}>None listed.</p>
      )}
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>Certifications</dt>
          <dd>{candidate.certifications.length > 0 ? candidate.certifications.join(', ') : '—'}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Languages</dt>
          <dd>{languages || '—'}</dd>
        </div>
      </dl>
    </>
  );
}
